import React, { useMemo } from "react";
import { Box, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { formatHours, rateProfileForGroup } from "./plannerQuoteRates";
import { formatMoney } from "./quotePricingMath";
import { buildWixJobQuote } from "./wixJobQuote";
import CustomerQuotePackagesView from "./CustomerQuotePackagesView";
import InternalQuoteEconomicsView from "./InternalQuoteEconomicsView";
import { plannerKindAccent } from "../planner/plannerTopics";

function money(n) {
    if (n == null || n === "") return "—";
    return formatMoney(n, "USD");
}

function hours(n) {
    return formatHours(n) || "—";
}

function Fact({ label, value, hint }) {
    return (
        <Paper variant="outlined" sx={{ px: 1.25, py: 1, minWidth: 0, flex: "1 1 140px" }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {value || "—"}
            </Typography>
            {hint ? (
                <Typography variant="caption" color="success.main">
                    {hint}
                </Typography>
            ) : null}
        </Paper>
    );
}

function KindChip({ kind }) {
    const label = kind || "component";
    return (
        <Chip
            size="small"
            label={label}
            sx={(theme) => {
                const accent = plannerKindAccent(theme, label);
                return {
                    bgcolor: alpha(accent, 0.22),
                    color: accent,
                    border: `1px solid ${alpha(accent, 0.45)}`,
                    fontWeight: 800,
                    textTransform: "capitalize",
                };
            }}
        />
    );
}

function RollupMetric({ label, value }) {
    if (value == null || value === "" || value === "—") return null;
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textTransform: "uppercase", letterSpacing: "0.08em" }}
            >
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {value}
            </Typography>
        </Box>
    );
}

function ComponentNode({ node, depth = 0, onEditItem }) {
    const variance = node.target == null ? null : node.variance;
    const childCount = node.children?.length || 0;
    const kind = node.lineKind || "component";
    return (
        <Box sx={{ mb: 1.25 }}>
            <Paper
                variant="outlined"
                sx={(theme) => {
                    const accent = plannerKindAccent(theme, kind);
                    return {
                        p: { xs: 1.25, sm: 1.5 },
                        bgcolor: alpha(accent, theme.palette.mode === "dark" ? 0.12 : 0.07),
                        borderColor: alpha(accent, 0.4),
                        borderLeft: `4px solid ${accent}`,
                    };
                }}
            >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            <KindChip kind={kind} />
                            {node.hideFromCustomer || node.customerVisible === false ? (
                                <Chip size="small" color="warning" variant="outlined" label="Internal only" />
                            ) : null}
                        </Stack>
                        <Typography variant="body2" sx={{ fontWeight: 800, mt: 0.75, lineHeight: 1.3 }}>
                            {node.title}
                        </Typography>
                    </Box>
                    {onEditItem ? (
                        <Tooltip title={`Edit ${node.title}`}>
                            <IconButton
                                color="primary"
                                onClick={() => onEditItem(node)}
                                aria-label={`Edit ${node.title}`}
                                sx={{ width: 44, height: 44, flexShrink: 0 }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </Stack>

                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mt: 1.25 }}
                >
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            Rough {money(node.roughTotal)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            PBR {money(node.builtUp)}
                            {node.contingencyPct
                                ? ` · +${money(node.riskLoadedTotal - node.builtUp)} risk · ${money(node.riskLoadedTotal)} loaded`
                                : ""}
                        </Typography>
                    </Box>
                    {node.target != null ? (
                        <Chip
                            size="small"
                            variant="outlined"
                            color={variance >= 0 ? "success" : "error"}
                            label={`${money(node.target)} target · ${variance >= 0 ? "+" : ""}${money(variance)}`}
                        />
                    ) : null}
                </Stack>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, minmax(0, 1fr))" },
                        gap: 1.25,
                        mt: 1.5,
                    }}
                >
                    <RollupMetric label="Child lines" value={childCount ? String(childCount) : null} />
                    <RollupMetric label="Labor" value={node.laborHours ? hours(node.laborHours) : null} />
                    <RollupMetric label="Labor $" value={node.laborCost ? money(node.laborCost) : null} />
                    <RollupMetric label="PBR charge" value={node.purchasesCustomer ? money(node.purchasesCustomer) : null} />
                    <RollupMetric label="Customer-supplied" value={node.customerPurchases ? money(node.customerPurchases) : null} />
                    <RollupMetric
                        label="Contingency"
                        value={node.contingencyPct ? `${node.contingencyPct}% · ${money(node.riskLoadedTotal)} loaded` : null}
                    />
                </Box>
                {node.lineKind === "purchase" && node.purchaseTreatment !== "pbr" ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        {node.purchaseTreatment === "customer"
                            ? "Customer purchase — excluded from PBR total"
                            : "Note only — excluded from PBR total"}
                    </Typography>
                ) : null}
            </Paper>
            {node.children?.length ? (
                <Box
                    sx={(theme) => ({
                        mt: 1,
                        ml: { xs: 0.5, sm: 1.5 },
                        pl: { xs: 0.75, sm: 1.5 },
                        borderLeft: `2px solid ${alpha(plannerKindAccent(theme, kind), 0.35)}`,
                    })}
                >
                    {node.children.map((child) => (
                        <ComponentNode key={child.id} node={child} depth={depth + 1} onEditItem={onEditItem} />
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}

export default function WixJobQuotePanel({ job, focus, items, groups, pricingSettings, onEditItem }) {
    const built = useMemo(() => (job ? buildWixJobQuote(job, items, groups, pricingSettings, focus) : null), [job, items, groups, pricingSettings, focus]);
    const groupMeta = useMemo(() => new Map((groups || []).map((g) => [g.key, g])), [groups]);
    const focusGroup = groupMeta.get(focus?.groupKey);
    const focusProfile = rateProfileForGroup({ group_key: focus?.groupKey, label: focusGroup?.label });
    const focusRateCost = (Number(focus?.quoteOwnLaborHours) || 0) * focusProfile.hourlyRate;

    return (
        <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Details & quote
            </Typography>

            {focus ? (
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
                    <Fact label="Status" value={focus.status === "need" ? "To-Do" : focus.status} />
                    <Fact label="Group" value={focusGroup?.label || focus.groupKey} hint={`${focusProfile.label} @ $${focusProfile.hourlyRate}/hr`} />
                    <Fact label="Priority" value={focus.priority} />
                    <Fact label="Lead time" value={focus.leadTime} />
                    <Fact label="Next" value={focus.nextAction} />
                    <Fact
                        label="Labor"
                        value={focus.laborEstimate || hours(focus.quoteOwnLaborHours)}
                        hint={focus.quoteChildLaborHours ? `Child est ${hours(focus.quoteChildLaborHours)}` : null}
                    />
                    <Fact
                        label="Line cost"
                        value={focus.costEstimate || money(focus.quoteOwnCost)}
                        hint={focus.quoteChildCost ? `Child est ${money(focus.quoteChildCost)}` : null}
                    />
                    <Fact
                        label="Hours × rate"
                        value={focus.quoteOwnLaborHours ? money(focusRateCost) : "—"}
                        hint={focus.quoteOwnLaborHours ? "From labor × group rate" : "No hours on this piece"}
                    />
                    <Fact
                        label="Purchases / materials"
                        value={focus.purchaseEstimate || money(focus.quoteOwnPurchases)}
                        hint={focus.quoteChildPurchases ? `Child est ${money(focus.quoteChildPurchases)}` : null}
                    />
                    <Fact
                        label="Own total"
                        value={focus.quoteOwnTotal != null ? money(focus.quoteOwnTotal) : "—"}
                        hint="This line only"
                    />
                    <Fact
                        label="Built-up"
                        value={focus.quoteChildTotal != null ? money(focus.quoteChildTotal) : "—"}
                        hint="From child items"
                    />
                    <Fact label="Purchase notes" value={focus.purchaseNotes} />
                </Stack>
            ) : null}

            {built?.components?.length ? (
                <Box sx={{ mb: 2 }}>
                    <CustomerQuotePackagesView components={built.components} />
                </Box>
            ) : null}

            {built?.components?.length ? (
                <InternalQuoteEconomicsView
                    components={built.components}
                    customerTotal={built.totals?.customerBudget}
                    actualHours={job?.actualHours}
                    extraMaterialCost={job?.extraMaterialCost}
                    sx={{ mb: 2 }}
                />
            ) : null}

            {built?.lines?.length ? (
                <Paper variant="outlined" sx={{ overflow: "auto" }}>
                    <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            Internal cost build
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Shop view only. {hours(built.totals.laborHours)} · labor {money(built.totals.rateCost)} · PBR charge {money(built.totals.purchasesCustomer)} ({money(built.totals.purchases)} cost) · customer-supplied {money(built.totals.customerPurchases)} · rough quote {money(built.totals.roughTotal)}
                        </Typography>
                    </Box>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ "& th": { fontWeight: 800, whiteSpace: "nowrap" } }}>
                                <TableCell>Group</TableCell>
                                <TableCell>Rate</TableCell>
                                <TableCell align="right">Hours</TableCell>
                                <TableCell align="right">Labor $</TableCell>
                                <TableCell align="right">PBR charge</TableCell>
                                <TableCell align="right">Customer-supplied</TableCell>
                                <TableCell align="right">Rough quote</TableCell>
                                <TableCell align="right">Line cost</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {built.lines.map((row) => (
                                <TableRow key={row.groupKey}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {row.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {row.pieces.length} piece{row.pieces.length === 1 ? "" : "s"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={`${row.profile.label} $${row.profile.hourlyRate}/hr`} />
                                    </TableCell>
                                    <TableCell align="right">{hours(row.laborHours)}</TableCell>
                                    <TableCell align="right">{money(row.rateCost)}</TableCell>
                                    <TableCell align="right">{money(row.purchasesCustomer)}</TableCell>
                                    <TableCell align="right">{money(row.customerPurchases)}</TableCell>
                                    <TableCell align="right">{money(row.roughTotal)}</TableCell>
                                    <TableCell align="right">{row.wixCost ? money(row.wixCost) : "—"}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    No labor hours, cost, or purchases on this job yet.
                </Typography>
            )}

            {built?.components?.length ? (
                <Paper variant="outlined" sx={{ mt: 2, overflow: "hidden" }}>
                    <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            Component roll-up
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Internal only — hours, rates, and PBR buy costs. The customer quote above shows package totals.
                        </Typography>
                    </Box>
                    <Box sx={{ p: 1 }}>
                        {built.components.map((component) => (
                            <ComponentNode key={component.id} node={component} onEditItem={onEditItem} />
                        ))}
                    </Box>
                </Paper>
            ) : null}
        </Box>
    );
}
